import { Button, Group, IconButton, iconSizes, Loader, Modal, Text, toast } from '@common/ui'
import { IconUserCheck, IconUserExclamation, IconUserPlus } from '@common/ui/icons'
import React, { useState } from 'react'
import { com } from '@eidu/web'
import useInviteAuthUser from '../../io/useInviteAuthUser'
import entityIdFromString = com.eidu.entity.entityIdFromString
import InvitationStatus = com.eidu.entity.user.InvitationStatus
import invitationStatusFromJsonString = com.eidu.entity.user.invitationStatusFromJsonString

export type CellContent = {
  value: string
  error?: string
}

export type InvitationCellParams = {
  value: CellContent | string | undefined
  row: {
    id: string
    phoneNumberLoginIdentifier?: string
    emailLoginIdentifier?: string
  }
}

type RenderCellFunction = (params: InvitationCellParams) => React.JSX.Element | null

const getRenderInvitationCell =
  (onInvitationSent?: () => void): RenderCellFunction =>
  ({ row, value }) => {
    const { invite, isPending: loading } = useInviteAuthUser()
    const [openDialog, setOpenDialog] = useState(false)

    const rawInvitationStatus = typeof value === 'object' ? value.value : value

    // Parse the serialized InvitationStatus data
    let invitationStatus: InvitationStatus | null = null
    try {
      if (rawInvitationStatus && rawInvitationStatus !== 'null') {
        invitationStatus = invitationStatusFromJsonString(rawInvitationStatus)
      }
    } catch (error) {
      console.error(error)
    }

    const entityId = entityIdFromString(row.id)
    const { phoneNumberLoginIdentifier: phoneNumber, emailLoginIdentifier: email } = row

    // Check if authentication credentials are present
    const hasCredentials = phoneNumber || email

    const sendInvitation = async (event?: React.MouseEvent) => {
      if (event) {
        event.stopPropagation()
      }
      try {
        await invite(entityId)

        onInvitationSent?.()

        let message = 'Invitation sent successfully'
        if (email && !phoneNumber) {
          message = 'Invitation email sent successfully'
        } else if (phoneNumber && !email) {
          message = 'Invitation SMS sent successfully'
        }

        toast.positive(message)
      } catch (error) {
        toast.negative('Failed to send invitation')
        console.error(error)
      } finally {
        setOpenDialog(false)
      }
    }

    const handleInvite = async (event: React.MouseEvent) => {
      // Don't do anything if already loading
      if (loading) {
        return
      }

      // Stop propagation to prevent row click
      event.stopPropagation()

      // For re-sending invitations, show confirmation dialog
      if (invitationStatus === InvitationStatus.Success || invitationStatus === InvitationStatus.Failure) {
        setOpenDialog(true)
        return
      }

      sendInvitation(event)
    }

    const handleCancelClick = (event: React.MouseEvent) => {
      event.stopPropagation()
      setOpenDialog(false)
    }

    // Don't show button if no credentials are present
    if (!hasCredentials || invitationStatus === null) {
      return null
    }

    const { icon: StateIcon, ...stateColors } =
      invitationStatus === InvitationStatus.Success
        ? { color: 'positive', c: 'var(--trellis-color-success)', icon: IconUserCheck }
        : invitationStatus === InvitationStatus.Failure
          ? { color: 'negative', c: 'var(--trellis-color-error)', icon: IconUserExclamation }
          : { color: 'neutral', c: 'var(--trellis-color-text-secondary)', icon: IconUserPlus }

    return (
      <>
        <IconButton
          {...stateColors}
          onClick={(event) => handleInvite(event)}
          disabled={loading}
          aria-label="Send invitation"
          data-testid="user-invitation-button"
        >
          {loading ? <Loader size="xs" /> : <StateIcon size={iconSizes.sm} stroke={1.5} />}
        </IconButton>

        <Modal
          opened={openDialog}
          onClose={() => setOpenDialog(false)}
          title="Re-send Invitation"
          data-testid="user-invitation-dialog"
        >
          <Text>Do you want to re-send the invitation to this user?</Text>
          <Group justify="flex-end" mt="md">
            <Button
              variant="default"
              onClick={handleCancelClick}
              disabled={loading}
              data-testid="user-invitation-cancel"
            >
              Cancel
            </Button>
            <Button onClick={(e) => sendInvitation(e)} disabled={loading} data-testid="user-invitation-resend">
              {loading ? <Loader size="xs" /> : 'Re-send'}
            </Button>
          </Group>
        </Modal>
      </>
    )
  }

export default getRenderInvitationCell
